import type { ReactNode } from 'react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { NodeMenu } from '@/components/custom/ogsm/node-menu'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { OwnerControl } from '@/components/custom/ogsm/owner-control'
import { StatusControl } from '@/components/custom/ogsm/status-control'
import type { OgsmBoardNode } from '@/types/api'

/**
 * The left cell of a band or row: owner, status, title and the node's menu.
 * Objectives and strategies share it; the depth only picks the wording.
 */
export function NodeHeading({
  node,
  titleVariant,
  tag,
  handle,
}: {
  node: OgsmBoardNode
  titleVariant: 'h5' | 'body'
  tag?: string | null
  /** A row's reorder handle, first in the control cluster. */
  handle?: ReactNode
}) {
  const { session, errorFor, editNode } = useBoardEdit()
  const kind =
    node.depth === 0 ? OGSM_COPY.kind.objective : OGSM_COPY.kind.strategy
  const editable = session.structureEditable
  const error = errorFor(node.id)

  return (
    <div className="flex min-w-0 items-start gap-2">
      {/* One title line tall, so the controls centre on the first line. */}
      <div
        className={cn(
          'flex h-lh shrink-0 items-center gap-2',
          titleVariant === 'h5' ? 'text-lg' : 'text-sm',
          'leading-normal'
        )}
      >
        {handle}
        <OwnerControl node={node} />
        <StatusControl node={node} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        {/* The node menu's "Edit" opens the same dialog; it is the keyboard path. */}
        <div
          className={cn('w-full', editable && 'cursor-pointer')}
          onDoubleClick={editable ? () => editNode(node) : undefined}
        >
          <Typography
            variant={titleVariant}
            className={cn(
              'break-words',
              // Strategy titles read lighter than the objective's.
              titleVariant === 'body' && 'font-light text-primary/70',
              !node.title && 'text-primary/60 italic'
            )}
          >
            {node.title || OGSM_COPY.untitled}
          </Typography>
        </div>
        {tag && (
          <Typography
            as="span"
            variant="body-sm"
            className="rounded-md border border-secondary/35 bg-secondary/10 px-2 py-0.5 text-secondary"
          >
            {tag}
          </Typography>
        )}
        {error && (
          <Typography
            variant="body-sm"
            role="alert"
            className="text-destructive"
          >
            {error}
          </Typography>
        )}
      </div>
      <NodeMenu node={node} kind={kind} />
    </div>
  )
}
